import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { runningPath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';
import './RunningRoutesPage.css';

const EMPTY_FORM = {
  title: '',
  description: '',
  distanceKm: '',
  surface: 'ROAD',
  difficulty: 'MODERATE',
  mapUrl: '',
  startLocation: '',
  endLocation: '',
  published: false,
};

const RunningRoutesPage = () => {
  const { id: clubId } = useParams();
  const { user, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const clubData = await api.getClubById(clubId);
      setClub(clubData);
      const isOwner = user?.id && clubData.adminUserId === user.id;
      const manage = !!(isOwner || isAdmin());
      setCanManage(manage);
      const data = await api.getRunningRoutes(clubId, !manage);
      setRoutes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const startEdit = (route) => {
    setEditingId(route.id);
    setForm({
      title: route.title || '',
      description: route.description || '',
      distanceKm: route.distanceKm != null ? String(route.distanceKm) : '',
      surface: route.surface || 'ROAD',
      difficulty: route.difficulty || 'MODERATE',
      mapUrl: route.mapUrl || '',
      startLocation: route.startLocation || '',
      endLocation: route.endLocation || '',
      published: !!route.published,
    });
    setSuccess('');
    setError('');
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    distanceKm: form.distanceKm ? Number(form.distanceKm) : null,
    surface: form.surface || null,
    difficulty: form.difficulty || null,
    mapUrl: form.mapUrl.trim() || null,
    startLocation: form.startLocation.trim() || null,
    endLocation: form.endLocation.trim() || null,
    published: !!form.published,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = buildPayload();
      if (editingId) {
        await api.updateRunningRoute(editingId, payload, user.id);
        setSuccess('Route updated');
      } else {
        await api.createRunningRoute(clubId, payload, user.id);
        setSuccess('Route created');
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save route');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (route) => {
    try {
      await api.publishRunningRoute(route.id, user.id, !route.published);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update publish state');
    }
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm('Delete this route?')) return;
    try {
      await api.deleteRunningRoute(routeId, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete route');
    }
  };

  const handlePhotos = async (routeId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      await api.uploadRunningRoutePhotos(routeId, files, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    }
  };

  if (loading) return <div className="loading">Loading routes...</div>;

  return (
    <div className="running-routes-page">
      <div className="running-routes-page__head">
        <div>
          <p className="running-routes-page__eyebrow">
            <Link to={runningPath(`/clubs/${clubId}`)}>{club?.name || 'Club'}</Link>
          </p>
          <h1>Routes &amp; tracks</h1>
          <p>Admins publish routes. Runners select one when joining a practice.</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {canManage && (
        <form className="running-routes-page__form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit route' : 'Publish a route'}</h2>
          <label>
            Title
            <input name="title" value={form.title} onChange={onChange} required maxLength={100} />
          </label>
          <label>
            Description
            <textarea name="description" value={form.description} onChange={onChange} rows={3} />
          </label>
          <div className="running-routes-page__row">
            <label>
              Distance (km)
              <input
                name="distanceKm"
                type="number"
                min="0.1"
                step="0.1"
                value={form.distanceKm}
                onChange={onChange}
              />
            </label>
            <label>
              Surface
              <select name="surface" value={form.surface} onChange={onChange}>
                <option value="ROAD">Road</option>
                <option value="TRAIL">Trail</option>
                <option value="TRACK">Track</option>
                <option value="MIXED">Mixed</option>
              </select>
            </label>
            <label>
              Difficulty
              <select name="difficulty" value={form.difficulty} onChange={onChange}>
                <option value="EASY">Easy</option>
                <option value="MODERATE">Moderate</option>
                <option value="HARD">Hard</option>
              </select>
            </label>
          </div>
          <label>
            Map / GPX / Strava URL
            <input name="mapUrl" value={form.mapUrl} onChange={onChange} placeholder="https://" />
          </label>
          <div className="running-routes-page__row">
            <label>
              Start location
              <input name="startLocation" value={form.startLocation} onChange={onChange} />
            </label>
            <label>
              End location
              <input name="endLocation" value={form.endLocation} onChange={onChange} />
            </label>
          </div>
          <label className="running-routes-page__check">
            <input
              type="checkbox"
              name="published"
              checked={form.published}
              onChange={onChange}
            />
            Published for runners
          </label>
          <div className="running-routes-page__form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Create route'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>
      )}

      <div className="running-routes-page__list">
        {routes.length === 0 && <p>No routes yet.</p>}
        {routes.map((route) => (
          <article key={route.id} className="running-routes-page__card">
            <div className="running-routes-page__card-top">
              <div>
                <h3>
                  {route.title}
                  {!route.published && <span className="running-routes-page__badge">Draft</span>}
                </h3>
                {route.distanceKm != null && (
                  <p className="running-routes-page__stat">{route.distanceKm} km</p>
                )}
                <p>
                  {[route.surface, route.difficulty].filter(Boolean).join(' · ')}
                </p>
                {route.description && <p>{route.description}</p>}
                {route.startLocation && <p>Start: {route.startLocation}</p>}
                {route.endLocation && <p>End: {route.endLocation}</p>}
                {route.mapUrl && (
                  <a href={route.mapUrl} target="_blank" rel="noreferrer">
                    Open map
                  </a>
                )}
              </div>
              {canManage && (
                <div className="running-routes-page__card-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => startEdit(route)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => togglePublish(route)}>
                    {route.published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => handleDelete(route.id)}>
                    Delete
                  </button>
                  <label className="btn btn-secondary running-routes-page__file">
                    Add photos
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(e) => handlePhotos(route.id, e.target.files)}
                    />
                  </label>
                </div>
              )}
            </div>
            <PhotoGallery photos={route.photoUrls || []} />
          </article>
        ))}
      </div>
    </div>
  );
};

export default RunningRoutesPage;
